import { useCallback, useEffect, useMemo, useState } from 'react';

import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { GripVertical, Trash2, Lock, CheckCircle2 } from 'lucide-react';

import { adminContent } from 'services/api';
import type { Course, Flow, CourseEligibility } from 'types';
import { CoursePicker } from 'components/admin/CoursePicker';
import { FlowPicker, type FlowPickerOption } from 'components/admin/FlowPicker';
import { CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';

export interface DraftCourseAssignment {
  course_id: number;
  name: string;
}

export interface DraftFlowAssignment {
  flow_id: number;
  name: string;
}

type ContentAssignmentEditorProps = {
  eligibilityByCourseId?: Record<number, CourseEligibility>;
  showCourses?: boolean;
  showFlows?: boolean;
  courseTitle: string;
  flowTitle?: string;
  flowDescription?: string;
  courseEmptyMessage: string;
  flowEmptyMessage: string;
  courseSaveLabel?: string;
  flowSaveLabel?: string;
  courses: DraftCourseAssignment[];
  flows: DraftFlowAssignment[];
  onCoursesChange: (next: DraftCourseAssignment[]) => void;
  onFlowsChange: (next: DraftFlowAssignment[]) => void;
  onSaveCourses: () => void | Promise<void>;
  onSaveFlows: () => void | Promise<void>;
  coursesDirty: boolean;
  flowsDirty: boolean;
  savingCourses?: boolean;
  savingFlows?: boolean;
};

const ContentAssignmentEditor: React.FC<ContentAssignmentEditorProps> = ({
  eligibilityByCourseId,
  showCourses = true,
  showFlows = true,
  courseTitle,
  flowTitle = 'Standalone Module Assignments',
  flowDescription,
  courseEmptyMessage,
  flowEmptyMessage,
  courseSaveLabel = 'Save',
  flowSaveLabel = 'Save',
  courses,
  flows,
  onCoursesChange,
  onFlowsChange,
  onSaveCourses,
  onSaveFlows,
  coursesDirty,
  flowsDirty,
  savingCourses = false,
  savingFlows = false,
}) => {
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [allFlows, setAllFlows] = useState<Flow[]>([]);
  const [loadingContent, setLoadingContent] = useState(false);
  const [contentError, setContentError] = useState(false);
  const [selectedCourseToAdd, setSelectedCourseToAdd] = useState('');
  const [selectedFlowToAdd, setSelectedFlowToAdd] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoadingContent(true);
      try {
        const [coursesRes, flowsRes] = await Promise.all([
          adminContent.userTools.listCourses(),
          adminContent.userTools.listFlows(),
        ]);
        setAllCourses(Array.isArray(coursesRes.data) ? coursesRes.data : coursesRes.data.results ?? []);
        setAllFlows(Array.isArray(flowsRes.data) ? flowsRes.data : flowsRes.data.results ?? []);
      } catch {
        setContentError(true);
      } finally {
        setLoadingContent(false);
      }
    };
    void load();
  }, []);

  const availableCourses = useMemo(() => {
    const selectedIds = new Set(courses.map((item) => item.course_id));
    return allCourses.filter((course) => !selectedIds.has(course.id));
  }, [allCourses, courses]);

  const availableFlows = useMemo(() => {
    const selectedIds = new Set(flows.map((item) => item.flow_id));
    const systemFreqs = new Set(['daily', 'weekly', 'monthly', 'onboarding', 'every_login']);
    return allFlows.filter((flow) => !selectedIds.has(flow.id) && !systemFreqs.has(flow.frequency ?? ''));
  }, [allFlows, flows]);

  const courseOptions = useMemo(
    () =>
      availableCourses.map((course) => ({
        value: String(course.id),
        label: course.name,
        searchText: course.description,
      })),
    [availableCourses],
  );

  const flowOptions = useMemo<FlowPickerOption[]>(
    () =>
      availableFlows.map((flow) => ({
        value: String(flow.id),
        label: flow.title || flow.name,
        searchText: flow.description,
        isQuiz: flow.is_quiz,
      })),
    [availableFlows],
  );

  const handleAddCourse = useCallback(() => {
    if (!selectedCourseToAdd) return;
    const course = allCourses.find((item) => String(item.id) === selectedCourseToAdd);
    if (!course) return;
    onCoursesChange([...courses, { course_id: course.id, name: course.name }]);
    setSelectedCourseToAdd('');
  }, [allCourses, courses, onCoursesChange, selectedCourseToAdd]);

  const handleAddFlow = useCallback(() => {
    if (!selectedFlowToAdd) return;
    const flow = allFlows.find((item) => String(item.id) === selectedFlowToAdd);
    if (!flow) return;
    onFlowsChange([...flows, { flow_id: flow.id, name: flow.title || flow.name }]);
    setSelectedFlowToAdd('');
  }, [allFlows, flows, onFlowsChange, selectedFlowToAdd]);

  const handleCourseDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;
    const src = result.source.index;
    const dst = result.destination.index;
    if (src === dst) return;
    const next = courses.slice();
    const [moved] = next.splice(src, 1);
    next.splice(dst, 0, moved);
    onCoursesChange(next);
  }, [courses, onCoursesChange]);

  const handleFlowDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;
    const src = result.source.index;
    const dst = result.destination.index;
    if (src === dst) return;
    const next = flows.slice();
    const [moved] = next.splice(src, 1);
    next.splice(dst, 0, moved);
    onFlowsChange(next);
  }, [flows, onFlowsChange]);

  return (
    <div className="space-y-6">
      {contentError && <p role="alert" className="text-sm text-destructive">Unable to load the course and module catalogue. Refresh this page to try again.</p>}
      {showCourses ? (
        <div className="space-y-4">
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle title={courseTitle} />
            <Button
              variant="default"
              size="auto"
              disabled={!coursesDirty || savingCourses}
              onClick={() => void onSaveCourses()}
            >
              {savingCourses ? 'Saving...' : courseSaveLabel}
            </Button>
          </div>

          <p className="text-sm text-muted-foreground">Assignments and access are separate. Courses with prerequisites remain visible but locked until all requirements are complete. Prerequisites are not assigned automatically.</p>
          <DragDropContext onDragEnd={handleCourseDragEnd}>
            <Droppable droppableId={`${courseTitle}-course-assignments`}>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="space-y-2 rounded-lg border border-border/40 bg-white/5 p-3 transition-colors"
                >
                  {courses.length === 0 ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">{courseEmptyMessage}</p>
                  ) : (
                    courses.map((item, index) => (
                      <Draggable key={item.course_id} draggableId={`course-${item.course_id}`} index={index}>
                        {(draggableProvided, draggableSnapshot) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            style={draggableProvided.draggableProps.style}
                            className={cn(
                              'flex items-center gap-3 rounded-md border bg-white/90 p-3 text-sm transition-shadow',
                              draggableSnapshot.isDragging && 'shadow-lg ring-2 ring-primary/50',
                            )}
                          >
                            <button
                              type="button"
                              className="rounded-md border border-white/20 bg-transparent p-1 text-muted-foreground transition hover:bg-white/10"
                              {...draggableProvided.dragHandleProps}
                              aria-label="Drag to reorder"
                            >
                              <GripVertical className="h-4 w-4" />
                            </button>
                            <div className="min-w-0 flex-1 text-foreground"><div className="break-words font-medium">{item.name}</div>{(() => {
                              const catalogue = allCourses.find(course => course.id === item.course_id);
                              const eligibility = eligibilityByCourseId?.[item.course_id];
                              if (eligibility && eligibility.prerequisites.length) return <div className="mt-2 space-y-1 text-xs"><p className={eligibility.is_locked ? 'font-medium text-amber-800' : 'font-medium text-green-800'}>{eligibility.is_locked ? 'Locked · prerequisites incomplete' : 'Prerequisites complete'}</p>{eligibility.prerequisites.map(prerequisite => <p key={prerequisite.id} className="flex items-start gap-1 text-muted-foreground">{prerequisite.is_completed ? <CheckCircle2 className="h-3 w-3 shrink-0" /> : <Lock className="h-3 w-3 shrink-0" />}<span>{prerequisite.name} · {prerequisite.is_completed ? 'Complete' : !prerequisite.is_assigned ? 'Not assigned to this learner' : `${Math.round(prerequisite.progress_percent)}% complete`}</span></p>)}</div>;
                              if (!catalogue?.prerequisites?.length) return null;
                              return <div className="mt-2 text-xs text-muted-foreground"><p className="flex items-start gap-1"><Lock className="h-3 w-3 shrink-0" /><span>Requires all: {catalogue.prerequisites.map(prerequisite => prerequisite.name).join(', ')}</span></p><p className="mt-1">Access depends on each learner’s completion and effective assignments.</p></div>;
                            })()}</div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => onCoursesChange(courses.filter((entry) => entry.course_id !== item.course_id))}
                              aria-label={`Remove course ${item.name}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <div className="flex max-w-lg gap-2">
            <div className="flex-1">
              <CoursePicker
                value={selectedCourseToAdd}
                onValueChange={setSelectedCourseToAdd}
                options={courseOptions}
                placeholder="Select a course to add..."
                loading={loadingContent}
                emptyMessage="No courses available"
                maxVisibleOptions={50}
              />
            </div>
            <Button variant="secondary" size="auto" onClick={handleAddCourse} disabled={!selectedCourseToAdd}>
              Add
            </Button>
          </div>
        </div>
      ) : null}

      {showFlows ? (
        <div className="space-y-4">
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle title={flowTitle} />
            <Button
              variant="default"
              size="auto"
              disabled={!flowsDirty || savingFlows}
              onClick={() => void onSaveFlows()}
            >
              {savingFlows ? 'Saving...' : flowSaveLabel}
            </Button>
          </div>
          {flowDescription ? (
            <p className="text-xs text-muted-foreground">{flowDescription}</p>
          ) : null}

          <DragDropContext onDragEnd={handleFlowDragEnd}>
            <Droppable droppableId={`${flowTitle}-flow-assignments`}>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="space-y-2 rounded-lg border border-border/40 bg-white/5 p-3 transition-colors"
                >
                  {flows.length === 0 ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">{flowEmptyMessage}</p>
                  ) : (
                    flows.map((item, index) => (
                      <Draggable key={item.flow_id} draggableId={`flow-${item.flow_id}`} index={index}>
                        {(draggableProvided, draggableSnapshot) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            style={draggableProvided.draggableProps.style}
                            className={cn(
                              'flex items-center gap-3 rounded-md border bg-white/90 p-3 text-sm transition-shadow',
                              draggableSnapshot.isDragging && 'shadow-lg ring-2 ring-primary/50',
                            )}
                          >
                            <button
                              type="button"
                              className="rounded-md border border-white/20 bg-transparent p-1 text-muted-foreground transition hover:bg-white/10"
                              {...draggableProvided.dragHandleProps}
                              aria-label="Drag to reorder"
                            >
                              <GripVertical className="h-4 w-4" />
                            </button>
                            <div className="flex-1 font-medium text-foreground">{item.name}</div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => onFlowsChange(flows.filter((entry) => entry.flow_id !== item.flow_id))}
                              aria-label={`Remove module ${item.name}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <div className="flex max-w-lg gap-2">
            <div className="flex-1">
              <FlowPicker
                value={selectedFlowToAdd}
                onValueChange={setSelectedFlowToAdd}
                options={flowOptions}
                placeholder="Select a module to add..."
                loading={loadingContent}
                emptyMessage="No modules available"
                maxVisibleOptions={50}
              />
            </div>
            <Button variant="secondary" size="auto" onClick={handleAddFlow} disabled={!selectedFlowToAdd}>
              Add
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ContentAssignmentEditor;
